/**
 * Asset Cache — Singleton cache with RefCount, LRU eviction, memory budget.
 *
 * Each engine instance creates its own AssetCache. Assets stay in cache after
 * being removed from the scene (fast re-use). Actual GPU disposal only
 * happens during LRU eviction or engine dispose.
 *
 * @see docs/ims-engine-api/04-asset-pipeline.md
 */

import type { LoadingPhase } from '../api/types';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type AssetType = 'glb' | 'hdr' | 'texture';

export interface CacheEntry {
  url: string;
  type: AssetType;
  /** The parsed asset (GLTF result, Texture, etc.). */
  data: unknown;
  /** Approximate size in KB. */
  sizeKB: number;
  /** How many consumers currently hold a reference. */
  refCount: number;
  /** Last access timestamp (ms). */
  lastAccess: number;
  /** Loading phase — critical assets are never evicted. */
  phase: LoadingPhase;
}

export interface AssetCacheOptions {
  /** Maximum memory budget in MB. Default: 300 */
  memoryBudgetMB?: number;
}

// ---------------------------------------------------------------------------
// GPU Resource Disposal Helpers
// ---------------------------------------------------------------------------

interface Disposable {
  dispose(): void;
}

interface MaterialLike extends Disposable {
  map?: Disposable | null;
  normalMap?: Disposable | null;
  roughnessMap?: Disposable | null;
  metalnessMap?: Disposable | null;
  aoMap?: Disposable | null;
  emissiveMap?: Disposable | null;
  envMap?: Disposable | null;
}

interface MeshLike {
  geometry?: Disposable;
  material?: MaterialLike | MaterialLike[];
}

interface ObjectLike {
  traverse?: (callback: (child: ObjectLike & MeshLike) => void) => void;
}

/**
 * AnimationClip-like shape. Each clip holds KeyframeTracks with
 * TypedArray values that should be cleaned up.
 */
interface AnimationClipLike extends Disposable {
  tracks?: Disposable[];
}

interface GLTFLike {
  scene?: ObjectLike;
  scenes?: ObjectLike[];
  /** AnimationClip[] — KeyframeTrack data that must be disposed. */
  animations?: AnimationClipLike[];
}

/** Recursively dispose all GPU resources of a GLTF scene. */
function disposeGLTF(gltf: GLTFLike): void {
  // Dispose geometry, materials, and texture maps
  const scenes = gltf.scenes ?? (gltf.scene ? [gltf.scene] : []);
  for (const scene of scenes) {
    scene.traverse?.((child) => {
      child.geometry?.dispose();
      const materials = Array.isArray(child.material)
        ? child.material
        : child.material
          ? [child.material]
          : [];
      for (const mat of materials) {
        mat.map?.dispose();
        mat.normalMap?.dispose();
        mat.roughnessMap?.dispose();
        mat.metalnessMap?.dispose();
        mat.aoMap?.dispose();
        mat.emissiveMap?.dispose();
        mat.envMap?.dispose();
        mat.dispose();
      }
    });
  }

  // Dispose AnimationClip data (KeyframeTracks hold TypedArrays)
  if (gltf.animations) {
    for (const clip of gltf.animations) {
      // Dispose individual tracks if they have dispose()
      if (clip.tracks) {
        for (const track of clip.tracks) {
          track.dispose?.();
        }
      }
      clip.dispose?.();
    }
  }
}

/** Dispose a single asset based on its type. */
function disposeAsset(entry: CacheEntry): void {
  switch (entry.type) {
    case 'glb':
      disposeGLTF(entry.data as GLTFLike);
      break;
    case 'hdr':
    case 'texture':
      (entry.data as Disposable).dispose();
      break;
  }
}

// ---------------------------------------------------------------------------
// AssetCache
// ---------------------------------------------------------------------------

const DEFAULT_BUDGET_MB = 300;

export class AssetCache {
  private _entries = new Map<string, CacheEntry>();
  private _budgetKB: number;
  private _pendingSizeKB = 0;
  /** Running total of cached sizes — avoids O(n) iteration in totalSizeKB. */
  private _totalSizeKB = 0;

  constructor(options?: AssetCacheOptions) {
    this._budgetKB = (options?.memoryBudgetMB ?? DEFAULT_BUDGET_MB) * 1024;
  }

  // --- Query ---------------------------------------------------------------

  /** Check whether a URL is cached. */
  has(url: string): boolean {
    return this._entries.has(url);
  }

  /** Get a cached entry (or undefined). */
  get(url: string): CacheEntry | undefined {
    const entry = this._entries.get(url);
    if (entry) entry.lastAccess = Date.now();
    return entry;
  }

  // --- Lifecycle -----------------------------------------------------------

  /**
   * Store an asset in the cache.
   * If the budget is exceeded, LRU eviction runs automatically.
   */
  set(
    url: string,
    type: AssetType,
    data: unknown,
    sizeKB: number,
    phase: LoadingPhase = 'lazy',
  ): CacheEntry {
    // Upsert
    const existing = this._entries.get(url);
    if (existing) {
      this._totalSizeKB += sizeKB - existing.sizeKB;
      existing.data = data;
      existing.sizeKB = sizeKB;
      existing.lastAccess = Date.now();
      existing.phase = phase;
      return existing;
    }

    // Evict BEFORE inserting so the new entry can never be its own victim
    this._pendingSizeKB = sizeKB;
    this._evictIfNeeded();
    this._pendingSizeKB = 0;

    const entry: CacheEntry = {
      url,
      type,
      data,
      sizeKB,
      refCount: 0,
      lastAccess: Date.now(),
      phase,
    };
    this._entries.set(url, entry);
    this._totalSizeKB += sizeKB;

    return entry;
  }

  /** Increment reference count (asset is being used by a manager). */
  acquire(url: string): void {
    const entry = this._entries.get(url);
    if (entry) {
      entry.refCount++;
      entry.lastAccess = Date.now();
    }
  }

  /** Decrement reference count (asset released by a manager). */
  release(url: string): void {
    const entry = this._entries.get(url);
    if (entry && entry.refCount > 0) {
      entry.refCount--;
    }
  }

  /**
   * Remove and dispose a specific entry.
   * Returns true if the entry existed and was removed.
   */
  delete(url: string): boolean {
    const entry = this._entries.get(url);
    if (!entry) return false;
    this._totalSizeKB -= entry.sizeKB;
    disposeAsset(entry);
    this._entries.delete(url);
    return true;
  }

  /** Dispose ALL entries. Called during engine dispose. */
  clear(): void {
    for (const entry of this._entries.values()) {
      disposeAsset(entry);
    }
    this._entries.clear();
    this._totalSizeKB = 0;
  }

  // --- Memory --------------------------------------------------------------

  /** Total cached size in KB (O(1) — maintained as a running counter). */
  get totalSizeKB(): number {
    return this._totalSizeKB;
  }

  /** Total cached size in MB (rounded to 1 decimal). */
  get memoryUsageMB(): number {
    return Math.round((this.totalSizeKB / 1024) * 10) / 10;
  }

  /** Number of cached entries. */
  get size(): number {
    return this._entries.size;
  }

  // --- LRU Eviction --------------------------------------------------------

  private _evictIfNeeded(): void {
    while (this._totalSizeKB + this._pendingSizeKB > this._budgetKB) {
      const victim = this._findEvictionCandidate();
      if (!victim) break; // Nothing evictable — we're over budget but all in use
      this._totalSizeKB -= victim.sizeKB;
      disposeAsset(victim);
      this._entries.delete(victim.url);
    }
  }

  /**
   * Find the oldest entry with refCount === 0 that is NOT phase 'critical'.
   */
  private _findEvictionCandidate(): CacheEntry | null {
    let oldest: CacheEntry | null = null;

    for (const entry of this._entries.values()) {
      if (entry.refCount > 0) continue;
      if (entry.phase === 'critical') continue;
      if (!oldest || entry.lastAccess < oldest.lastAccess) {
        oldest = entry;
      }
    }

    return oldest;
  }
}
